import { useCallback, useEffect, useRef, useState } from "react";
import {
  assignAdminConversationAPI,
  fetchCopilotAgentsAPI,
  fetchCopilotStatsAPI,
  getAdminConversationAPI,
  listAdminConversationsAPI,
  markAdminConversationReadAPI,
  replyAdminConversationAPI,
  setAdminConversationStatusAPI,
  uploadAdminAttachmentAPI,
} from "../../utils/api/copilotAdmin";
import { fetchCopilotMetaAPI } from "../../utils/api/copilot";
import { announceCopilotChange } from "./useCopilotUnresolved";
import { ADMIN_TEXT, adminErrorMessage } from "./copilotAdminText";
import { useConfirmedAction } from "./useConfirmedAction";

// State and actions of the admin inbox. Everything goes through the Phase 1
// admin API: filtering, search and pagination are server side, nothing loads
// "all" conversations or messages.
//
// Stale responses never overwrite newer data: every list/detail/stat request
// takes a ticket, and only the latest ticket may write its result.

const PAGE = 20;
const POLL_MS = 15000;
const SEARCH_DEBOUNCE_MS = 300;

export const DEFAULT_FILTERS = Object.freeze({ status: "unresolved", category: "", assignedTo: "", search: "", from: "", to: "", unread: false });

const uuid = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((m) => [m.id, m]));
  incoming.forEach((m) => byId.set(m.id, m));
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
};

const toParams = (filters) => {
  const params = {};
  if (filters.status) params.status = filters.status;
  if (filters.category) params.category = filters.category;
  if (filters.assignedTo) params.assignedTo = filters.assignedTo;
  if (filters.search.trim()) params.search = filters.search.trim();
  // date inputs are local days: from = start of day, to = end of day
  if (filters.from) params.from = new Date(`${filters.from}T00:00:00`).toISOString();
  if (filters.to) params.to = new Date(`${filters.to}T23:59:59.999`).toISOString();
  if (filters.unread) params.unread = "true";
  return params;
};

export const hasActiveFilters = (filters) =>
  Object.keys(DEFAULT_FILTERS).some((key) => filters[key] !== DEFAULT_FILTERS[key]);

const EMPTY_LIST = { items: [], status: "loading", error: null, hasMore: false, cursor: null, loadingMore: false };

export function useCopilotInbox({ lang, selectedId, canStats, canAssign, visible = true }) {
  const confirmation = useConfirmedAction(lang);
  const t = ADMIN_TEXT[lang] || ADMIN_TEXT.en;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState(EMPTY_LIST);
  const [detail, setDetail] = useState({ status: "idle", conversation: null, messages: [], pending: [] });
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState({ status: "idle", items: [] });
  const [categories, setCategories] = useState(null);
  const [busy, setBusy] = useState(null); // "assign" | "status" | null
  const [notice, setNotice] = useState(null);
  const [drafts, setDrafts] = useState({});

  const tickets = useRef({ list: 0, detail: 0, stats: 0 });
  const controllers = useRef({});
  const live = useRef({});
  const pendingRef = useRef({});
  const draftsRef = useRef({});
  const busyRef = useRef(false);
  const sendingKeys = useRef(new Set());
  live.current = { filters, list, selectedId, visible, t };

  const abortable = (name) => {
    controllers.current[name]?.abort();
    const controller = new AbortController();
    controllers.current[name] = controller;
    return controller.signal;
  };

  // Leaving the page cancels whatever is still in flight (no work for a screen that is gone).
  useEffect(() => () => Object.values(controllers.current).forEach((controller) => controller.abort()), []);

  // ---- categories (labels come from the backend) ----------------------------
  useEffect(() => {
    fetchCopilotMetaAPI().then((data) => setCategories(data.categories), () => {});
  }, []);

  // ---- statistics ----------------------------------------------------------------
  const loadStats = useCallback(async () => {
    if (!canStats) return;
    const ticket = (tickets.current.stats += 1);
    try {
      const data = await fetchCopilotStatsAPI(abortable("stats"));
      if (ticket === tickets.current.stats) setStats(data.stats);
    } catch (error) {
      if (error?.name !== "AbortError" && ticket === tickets.current.stats) setStats((prev) => prev);
    }
  }, [canStats]);

  // ---- list ----------------------------------------------------------------------
  // silent: background refresh that keeps what is on screen (and the pages
  // already loaded) instead of flashing a loading state.
  const loadList = useCallback(async ({ silent = false } = {}) => {
    const ticket = (tickets.current.list += 1);
    const { filters: current, list: shown } = live.current;
    if (!silent) setList(EMPTY_LIST);
    try {
      const limit = silent ? Math.min(100, Math.max(PAGE, shown.items.length)) : PAGE;
      const data = await listAdminConversationsAPI({ ...toParams(current), limit }, abortable("list"));
      if (ticket !== tickets.current.list) return;
      setList({ items: data.conversations, status: "ready", error: null, hasMore: data.hasMore, cursor: data.nextCursor, loadingMore: false });
    } catch (error) {
      if (error?.name === "AbortError" || ticket !== tickets.current.list) return;
      if (!silent) setList({ ...EMPTY_LIST, status: "error", error: adminErrorMessage(error, live.current.t) });
    }
  }, []);

  const loadMore = useCallback(async () => {
    const { list: shown, filters: current } = live.current;
    if (!shown.hasMore || !shown.cursor || shown.loadingMore) return;
    const ticket = tickets.current.list;
    setList((prev) => ({ ...prev, loadingMore: true }));
    try {
      const data = await listAdminConversationsAPI({ ...toParams(current), limit: PAGE, cursor: shown.cursor });
      if (ticket !== tickets.current.list) return; // filters changed meanwhile
      setList((prev) => {
        const known = new Set(prev.items.map((c) => c.id));
        return { ...prev, items: [...prev.items, ...data.conversations.filter((c) => !known.has(c.id))], hasMore: data.hasMore, cursor: data.nextCursor, loadingMore: false };
      });
    } catch (error) {
      if (ticket === tickets.current.list) setList((prev) => ({ ...prev, loadingMore: false, error: adminErrorMessage(error, live.current.t) }));
    }
  }, []);

  // Filters (debounced search) -> reload from the first page.
  useEffect(() => {
    const timer = setTimeout(() => setFilters((prev) => (prev.search === searchText ? prev : { ...prev, search: searchText })), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    loadList({ silent: false });
  }, [filters, loadList]);

  const updateFilters = useCallback((patch) => setFilters((prev) => ({ ...prev, ...patch })), []);
  const applyFilters = useCallback((next) => {
    setSearchText(next.search ?? "");
    setFilters(next);
  }, []);
  const clearFilters = useCallback(() => {
    setSearchText("");
    setFilters(DEFAULT_FILTERS);
  }, []);

  // ---- selected conversation -----------------------------------------------------
  const markRead = useCallback(async (id) => {
    try {
      const data = await markAdminConversationReadAPI(id);
      if (live.current.selectedId !== id) return;
      setDetail((prev) => (prev.conversation?.id === id ? { ...prev, conversation: { ...prev.conversation, unreadCount: data.unreadCount } } : prev));
      setList((prev) => ({ ...prev, items: prev.items.map((c) => (c.id === id ? { ...c, unreadCount: data.unreadCount } : c)) }));
      if (data.marked) {
        loadStats();
        announceCopilotChange();
      }
    } catch {
      // retried by the next refresh
    }
  }, [loadStats]);

  const loadDetail = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    const ticket = (tickets.current.detail += 1);
    if (!silent) setDetail({ status: "loading", conversation: null, messages: [], pending: pendingRef.current[id] || [] });
    try {
      const data = await getAdminConversationAPI(id, { limit: 30, signal: abortable("detail") });
      if (ticket !== tickets.current.detail || live.current.selectedId !== id) return;
      setDetail((prev) => {
        const same = prev.conversation?.id === id;
        return {
          status: "ready",
          error: null,
          conversation: data.conversation,
          messages: mergeMessages(same ? prev.messages : [], data.messages),
          hasMore: same && prev.messages.length ? prev.hasMore : data.hasMore,
          nextBefore: same && prev.messages.length ? prev.nextBefore : data.nextBefore,
          pending: pendingRef.current[id] || [],
          prepended: same ? prev.prepended : undefined,
        };
      });
      setList((prev) => ({ ...prev, items: prev.items.map((c) => (c.id === id ? { ...c, ...data.conversation } : c)) }));
      if (data.conversation.unreadCount > 0 && live.current.visible) markRead(id);
    } catch (error) {
      if (error?.name === "AbortError" || ticket !== tickets.current.detail || live.current.selectedId !== id) return;
      if (error.status === 404 || error.status === 403) setDetail({ status: "notfound", conversation: null, messages: [], pending: [] });
      else if (!silent) setDetail((prev) => ({ ...prev, status: prev.messages.length ? "ready" : "error", error: adminErrorMessage(error, live.current.t) }));
    }
  }, [markRead]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId, { silent: false });
    else {
      tickets.current.detail += 1;
      setDetail({ status: "idle", conversation: null, messages: [], pending: [] });
    }
  }, [selectedId, loadDetail]);

  const loadEarlier = useCallback(async () => {
    const id = live.current.selectedId;
    const current = detailRef.current;
    if (!id || !current.hasMore || current.loadingEarlier) return;
    const ticket = tickets.current.detail;
    setDetail((prev) => ({ ...prev, loadingEarlier: true }));
    try {
      const data = await getAdminConversationAPI(id, { before: current.nextBefore, limit: 30 });
      if (ticket !== tickets.current.detail || live.current.selectedId !== id) return;
      setDetail((prev) => ({ ...prev, messages: mergeMessages(prev.messages, data.messages), hasMore: data.hasMore, nextBefore: data.nextBefore, loadingEarlier: false, prepended: Date.now() }));
    } catch (error) {
      setDetail((prev) => ({ ...prev, loadingEarlier: false, error: adminErrorMessage(error, live.current.t) }));
    }
  }, []);
  const detailRef = useRef(detail);
  detailRef.current = detail;

  // ---- polling ---------------------------------------------------------------------
  useEffect(() => {
    if (!visible) return undefined;
    loadStats();
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadList({ silent: true });
      loadStats();
      if (live.current.selectedId && !sendingKeys.current.size) loadDetail(live.current.selectedId, { silent: true });
    }, POLL_MS);
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      loadList({ silent: true });
      loadStats();
      if (live.current.selectedId) loadDetail(live.current.selectedId, { silent: true });
    };
    document.addEventListener("visibilitychange", onReturn);
    // back online after a connection loss: refresh everything once
    window.addEventListener("online", onReturn);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("online", onReturn);
    };
  }, [visible, loadList, loadStats, loadDetail]);

  // ---- agents (assignment dialog) ----------------------------------------------------
  const loadAgents = useCallback(async () => {
    if (!canAssign) return;
    setAgents((prev) => ({ ...prev, status: "loading" }));
    try {
      const data = await fetchCopilotAgentsAPI();
      setAgents({ status: "ready", items: data.agents });
    } catch {
      setAgents((prev) => ({ ...prev, status: "error" }));
    }
  }, [canAssign]);

  // ---- after any change: keep list, detail, stats and the sidebar badge in step -------
  const refreshAfterChange = useCallback((conversation) => {
    if (conversation) {
      setDetail((prev) => (prev.conversation?.id === conversation.id ? { ...prev, conversation } : prev));
      setList((prev) => ({ ...prev, items: prev.items.map((c) => (c.id === conversation.id ? { ...c, ...conversation } : c)) }));
    }
    loadList({ silent: true });
    loadStats();
    announceCopilotChange();
  }, [loadList, loadStats]);

  const runAction = useCallback(async (name, action, doneText) => {
    if (busyRef.current) return false; // repeated clicks: one action at a time
    busyRef.current = true;
    setBusy(name);
    try {
      const data = await action();
      refreshAfterChange(data.conversation);
      // The detail also holds the event trail (assignment / status changes).
      loadDetail(live.current.selectedId, { silent: true });
      setNotice({ type: "success", text: doneText });
      return true;
    } catch (error) {
      setNotice({ type: "error", text: adminErrorMessage(error, live.current.t) });
      if (error.status === 409 || error.status === 404) {
        loadDetail(live.current.selectedId, { silent: true });
        loadList({ silent: true });
      }
      return false;
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }, [refreshAfterChange, loadDetail, loadList]);

  const assign = useCallback((assigneeId) => {
    const id = live.current.selectedId;
    return runAction("assign", () => assignAdminConversationAPI(id, assigneeId), assigneeId ? live.current.t.actionDone.assigned : live.current.t.actionDone.unassigned);
  }, [runAction]);

  const changeStatus = useCallback((status, note) => {
    const id = live.current.selectedId;
    return runAction("status", () => setAdminConversationStatusAPI(id, status, note), live.current.t.actionDone.status);
  }, [runAction]);

  // ---- replies / notes ---------------------------------------------------------------
  const setDraft = useCallback((id, patch) => {
    draftsRef.current = { ...draftsRef.current, [id]: { text: "", files: [], internal: false, ...(draftsRef.current[id] || {}), ...patch } };
    setDrafts(draftsRef.current);
  }, []);

  const setPending = useCallback((id, fn) => {
    pendingRef.current[id] = fn(pendingRef.current[id] || []);
    setDetail((prev) => (prev.conversation?.id === id || live.current.selectedId === id ? { ...prev, pending: pendingRef.current[id] } : prev));
  }, []);

  const deliver = useCallback(async (id, key) => {
    const entry = (pendingRef.current[id] || []).find((p) => p.key === key);
    if (!entry || sendingKeys.current.has(key)) return;
    sendingKeys.current.add(key);
    setPending(id, (items) => items.map((p) => (p.key === key ? { ...p, status: "sending", error: null } : p)));
    try {
      const uploaded = [...entry.uploaded];
      for (const file of entry.files.slice(uploaded.length)) {
        const result = await uploadAdminAttachmentAPI(id, file);
        uploaded.push(result.attachment.id);
        setPending(id, (items) => items.map((p) => (p.key === key ? { ...p, uploaded: [...uploaded] } : p)));
      }
      const result = await replyAdminConversationAPI(id, { text: entry.text, attachments: uploaded, internal: entry.internal, clientMessageId: key });
      setPending(id, (items) => items.filter((p) => p.key !== key));
      setDetail((prev) => (prev.conversation?.id === id ? { ...prev, messages: mergeMessages(prev.messages, [result.message]) } : prev));
      if (live.current.selectedId === id) {
        refreshAfterChange(null);
        loadDetail(id, { silent: true }); // status (pending), assignment, event trail
      }
      if (entry.internal) setNotice({ type: "success", text: live.current.t.actionDone.note });
    } catch (error) {
      setPending(id, (items) => items.map((p) => (p.key === key ? { ...p, status: "failed", error: adminErrorMessage(error, live.current.t) } : p)));
      if (error.status === 409 && live.current.selectedId === id) loadDetail(id, { silent: true });
    } finally {
      sendingKeys.current.delete(key);
    }
  }, [setPending, refreshAfterChange, loadDetail]);

  const send = useCallback((id) => {
    const draft = draftsRef.current[id];
    const text = (draft?.text || "").trim();
    const files = draft?.files || [];
    if ((!text && !files.length) || text.length > 5000) return;
    const key = uuid();
    setPending(id, (items) => [...items, { key, text, files, uploaded: [], internal: Boolean(draft?.internal), status: "sending", error: null, createdAt: new Date().toISOString() }]);
    setDraft(id, { text: "", files: [] });
    deliver(id, key);
  }, [deliver, setDraft, setPending]);

  const retry = useCallback((id, key) => deliver(id, key), [deliver]);
  const discardPending = useCallback((id, key) => setPending(id, (items) => items.filter((p) => p.key !== key)), [setPending]);

  return {
    t, filters, searchText, setSearchText, updateFilters, applyFilters, clearFilters,
    list, loadList, loadMore, detail, loadDetail, loadEarlier, stats, loadStats,
    agents, loadAgents, categories, busy, notice, dismissNotice: () => setNotice(null),
    assign, changeStatus: (status, note) => {
      if (status === "resolved") return changeStatus(status, note);
      const id = selectedId;
      return confirmation.ask(() => runAction("status", () => setAdminConversationStatusAPI(id, status, note), t.actionDone.status), "status", t.statusNames[status]);
    },
    confirmationDialog: confirmation.dialog, drafts, setDraft, send, retry, discardPending,
  };
}
